import { tryCall, tryNew } from '@/helpers/dto2resource';

import OfertaDto from '../dto/OfertaDto';
import MeioPagamento, { opcoesMeioPagamento } from '../enums/MeioPagamento';
import DetalheOfertaResource from './DetalheOfertaResource';

/**
 * Chave que identifica o mesmo plano (parcelas + entrada + tipo), independente da data de vencimento.
 * Extraída para ser reaproveitada por seletores que agrupam ofertas sem uma instância completa de `OfertaResource`.
 */
export function calcularAgrupamentoOferta(
  oferta: Pick<OfertaResource, 'quantidadeParcelas' | 'quantidadeParcelasMaisEntrada' | 'negociacaoParcial'>
): string {
  return `${oferta.quantidadeParcelas}-${oferta.quantidadeParcelasMaisEntrada}-${oferta.negociacaoParcial}`;
}

export default class OfertaResource {
  id: number | string;
  prioridade: number | null;
  descricao: string;
  dividaId: number;
  quantidadeParcelas: number;
  valorParcela: number;
  valorEntrada: number;
  dataPrimeiroBoleto: Date;
  dataProximoBoleto?: Date;
  valorTotal: number;
  possuiEntrada: boolean;
  quantidadeParcelasMaisEntrada: number;
  valorNegociado: number;
  informacoesAdicionais?: Record<string, unknown>;
  negociacaoParcial: boolean;
  detalhe?: DetalheOfertaResource;
  necessitaDetalhamento: boolean;
  meiosPagamentosDisponiveis: [MeioPagamento, MeioPagamento[]][];

  constructor(resource: OfertaDto) {
    this.id = resource.id;
    this.prioridade = resource.prioridade;
    this.descricao = resource.descricao;
    this.dividaId = resource.dividaId;
    this.quantidadeParcelas = resource.quantidadeParcelas;
    this.valorParcela = resource.valorParcela;
    this.valorEntrada = resource.valorEntrada;
    this.dataPrimeiroBoleto = new Date(resource.dataPrimeiroBoleto);
    this.dataProximoBoleto = tryCall(d => new Date(d), resource.dataProximoBoleto);
    this.valorTotal = resource.detalhe?.valores?.valorTotal ?? resource.valorTotal;
    this.possuiEntrada = resource.possuiEntrada;
    this.quantidadeParcelasMaisEntrada = resource.quantidadeParcelasMaisEntrada;
    this.valorNegociado = resource.valorNegociado;
    this.informacoesAdicionais = resource.informacoesAdicionais ?? undefined;
    this.negociacaoParcial = !!resource.negociacaoParcial;
    this.detalhe = tryNew(DetalheOfertaResource, resource.detalhe);
    this.necessitaDetalhamento = !!resource.necessitaDetalhamento;

    if (resource.meiosPagamentosDisponiveis) {
      // Verifica se oferta é do cache para converter e suportar deserialização do cache
      const jaConvertido =
        Array.isArray(resource.meiosPagamentosDisponiveis) &&
        resource.meiosPagamentosDisponiveis.length > 0 &&
        Array.isArray(resource.meiosPagamentosDisponiveis[0]);

      if (jaConvertido) {
        this.meiosPagamentosDisponiveis = resource.meiosPagamentosDisponiveis as unknown as [
          MeioPagamento,
          MeioPagamento[],
        ][];
      } else {
        const convertido: [MeioPagamento, MeioPagamento[]][] = Object.entries(resource.meiosPagamentosDisponiveis).map(
          ([key, value]) => [parseInt(key, 10), value.map((v: number) => v)]
        );
        this.meiosPagamentosDisponiveis = convertido;
      }
      this.ordenarMeiosPagamentosDisponiveis();
    } else {
      this.meiosPagamentosDisponiveis = [
        [MeioPagamento.Boleto, resource.quantidadeParcelasMaisEntrada > 1 ? [MeioPagamento.Boleto] : []],
      ];
    }
  }

  ordenarMeiosPagamentosDisponiveis = () => {
    const ordem = [MeioPagamento.Pix, MeioPagamento.Boleto, MeioPagamento.DebitoEmConta];
    const meiosPagamentoPrimeiraOrdenados = this.meiosPagamentosDisponiveis.sort(
      ([a], [b]) => ordem.indexOf(a) - ordem.indexOf(b)
    );

    for (let index = 0; index < meiosPagamentoPrimeiraOrdenados.length; index += 1) {
      const meiosPagamentoDemaisOrdenados = meiosPagamentoPrimeiraOrdenados[index][1].sort(
        (a, b) => ordem.indexOf(a) - ordem.indexOf(b)
      );

      meiosPagamentoPrimeiraOrdenados[index] = [
        meiosPagamentoPrimeiraOrdenados[index][0],
        meiosPagamentoDemaisOrdenados,
      ];
    }

    this.meiosPagamentosDisponiveis = meiosPagamentoPrimeiraOrdenados;
  };

  get valorDesconto(): number {
    return this.detalhe?.valores?.valorDesconto ?? this.valorNegociado - this.valorTotal;
  }

  get agrupamento(): string {
    return calcularAgrupamentoOferta(this);
  }

  get percentualDesconto(): number {
    const percentualDescontoDetalhe = this.detalhe?.valores?.percentualDesconto;
    if (percentualDescontoDetalhe != null && percentualDescontoDetalhe > 0) {
      return percentualDescontoDetalhe;
    }
    if (this.valorDesconto < 0) {
      return 0;
    }
    return Math.floor((this.valorDesconto * 100) / this.valorNegociado);
  }

  static normalizeId(ofertaId: number | string): OfertaResource['id'] {
    const id = ofertaId.toString();
    return /^\d+$/.test(id) ? parseInt(id, 10) : id;
  }

  get tipoNegociacao() {
    return this.informacoesAdicionais?.tipoNegociacao;
  }

  get permiteEscolherMeioPagamento() {
    const meiosPagamentoPrimeiraParcela = this.meiosPagamentosDisponiveis.filter(([meioPagamento]) =>
      opcoesMeioPagamento.includes(meioPagamento)
    );

    return meiosPagamentoPrimeiraParcela.length > 1;
  }

  priorizarMeiosPagamento(
    restringirMeioPagamentoFormalizacao: boolean | undefined
  ): [MeioPagamento, MeioPagamento[]][] {
    const pix = this.meiosPagamentosDisponiveis.find(([key]) => key === MeioPagamento.Pix);
    const permiteEscolherMeioPagamento = this.permiteEscolherMeioPagamento && restringirMeioPagamentoFormalizacao;
    if (pix && permiteEscolherMeioPagamento) {
      return [pix];
    }
    return this.meiosPagamentosDisponiveis;
  }
}
